import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CellInfo } from 'react-table';
import { IBoxScoreDiscrepancy } from 'services/BoxScoreManager';
import { IBoxScorePlayer } from 'interfaces/boxScore/IBoxScorePlayer';
import { emptyGuid } from 'constants/Constants';
import { ICombinedBoxScorePitcher } from 'interfaces/boxScore/ICombinedBoxScorePitcher';
import { ICombinedBoxScoreBatter } from 'interfaces/boxScore/ICombinedBoxScoreBatter';
import { PositionName, SecondValue, SourceValue, StyledReactTable, TeamName, TeamPositionWrapper } from './styles/GridViewStyles';
import NoBoxScoreData from '../NoData/NoBoxScoreData';
import Loader from '../Loader/Loader';

interface IProps {
  data: ICombinedBoxScorePitcher[] | ICombinedBoxScoreBatter[];
  isPitcherView: boolean;
  selectedIds: string[];
  onPlayerSelectionChange: (isChecked: boolean, playerId: string) => void;
  discrepancies: IBoxScoreDiscrepancy[];
  isLoading: boolean;
}

interface BoxSelection {
  index: string | number;
  playerId: string;
  dataKey: string | undefined;
}

const PITCHER_WIDTH = 45;
const BATTER_WIDTH = 37;
export const GridView: React.FC<IProps> = observer((props) => {
  const { data, isPitcherView, selectedIds, onPlayerSelectionChange, discrepancies, isLoading } = props;
  const [selection, setSelection] = useState<BoxSelection>();

  const getIdOrName = (player: IBoxScorePlayer) => {
    if (player.playerId === emptyGuid) {
      return player.name;
    }
    return player.playerId;
  };

  const renderNameCell = (cellInfo: CellInfo) => {
    const discrepancy = discrepancies.find((d) => d.playerId === cellInfo.original.playerId);
    return (
      <div className={`name-cell ${discrepancy && discrepancy.discrepantProperties.includes('name') ? 'discrepant' : ''}`}>
        {cellInfo.value}
      </div>
    );
  };

  const onRowClick = (cellInfo: CellInfo) => {
    if (selection) {
      onPlayerSelectionChange(false, selection.playerId);
    }

    onPlayerSelectionChange(true, getIdOrName(cellInfo.original));
    setSelection({
      index: cellInfo.index,
      playerId: getIdOrName(cellInfo.original),
      dataKey: cellInfo.column.id,
    });
  };

  const renderDataCell = (cellInfo: CellInfo) => {
    const discrepancy = discrepancies.find((d) => d.playerId === cellInfo.original.playerId);
    let className = '';
    if (discrepancy && discrepancy.discrepantProperties.includes(cellInfo.column.id as string)) {
      className = 'discrepant';
    }
    if (selection?.index === cellInfo.index && selection?.dataKey === cellInfo.column.id) {
      className += ' selected';
    }

    return (
      <div className={`content-cell ${className}`} onClick={() => onRowClick(cellInfo)}>
        <SourceValue>{cellInfo.value.sourceValue ? cellInfo.value.sourceValue : ''}</SourceValue>
        <SecondValue>{cellInfo.value.secondValue ? cellInfo.value.secondValue : ''}</SecondValue>
      </div>
    );
  };

  const renderTeamPositionCell = (cellInfo: CellInfo) => {
    return (
      <TeamPositionWrapper>
        <TeamName>TM</TeamName>
        <PositionName>SS</PositionName>
      </TeamPositionWrapper>
    );
  };
  return (
    <div className='box-score-table'>
      {isLoading ? (
        <Loader />
      ) : discrepancies.length > 0 ? (
        <StyledReactTable
          data={data}
          columns={
            isPitcherView
              ? [
                  {
                    Header: '',
                    accessor: 'teamAndPosition',
                    width: BATTER_WIDTH,
                    Cell: (cellInfo: any) => renderTeamPositionCell(cellInfo),
                  },
                  { Header: 'Player', accessor: 'name', width: 200, Cell: (cellInfo: any) => renderNameCell(cellInfo) },
                  { Header: 'IP', accessor: 'ip', width: PITCHER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'H', accessor: 'h', width: PITCHER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'SO', accessor: 'so', width: PITCHER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'BB', accessor: 'bb', width: PITCHER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                ]
              : [
                  {
                    Header: '',
                    accessor: 'teamAndPosition',
                    width: BATTER_WIDTH,
                    Cell: (cellInfo: any) => renderTeamPositionCell(cellInfo),
                  },
                  { Header: 'Player', accessor: 'name', width: 200, Cell: (cellInfo: any) => renderNameCell(cellInfo) },
                  { Header: 'AB', accessor: 'ab', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'H', accessor: 'h', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'BB', accessor: 'bb', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'HBP', accessor: 'hbp', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'SAC', accessor: 'sac', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: '2B', accessor: '2B', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: '3B', accessor: '3B', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: 'HR', accessor: 'hr', width: BATTER_WIDTH, Cell: (cellInfo: any) => renderDataCell(cellInfo) },
                  { Header: '', accessor: 'dp', width: 20, Cell: () => <></> },
                ]
          }
          showPagination={false}
          resizable={false}
          sortable={false}
          // pageSize={discrepancies.length} // TODO: set minimal height for table
        />
      ) : (
        <NoBoxScoreData />
      )}
    </div>
  );
});
